import type { ActivationFlowData } from "app/pages/Customer/FixedPhone/Checkout/utils/fixed-phone-storage";
import {
  getActivationFlowData,
  getFixedPhonePurchaseFlowData,
  saveActivationFlowData,
} from "app/pages/Customer/FixedPhone/Checkout/utils/fixed-phone-storage";
import { ActivationEntryType } from "app/pages/Customer/Activation/activation-entry-types";

/**
 * How the device/SIM defines included home-phone plan(s).
 * - `monthly` / `yearly`: exactly one included (from explicit API booleans or legacy single flag).
 * - `both`: both `isMonthlyPlanIncluded` and `isYearlyPlanIncluded` are true — user must pick on choose-plan.
 * - `neither`: both flags explicitly false — no included plan; user still picks monthly/yearly on choose-service-plan.
 * - `unknown`: booleans missing — caller may toast or fall back.
 */
export type DeviceIncludedPlanResolution =
  | "monthly"
  | "yearly"
  | "both"
  | "neither"
  | "unknown";

function readPlanInclusionBooleans(simOrDevice: any): {
  monthly?: boolean;
  yearly?: boolean;
} {
  const d = simOrDevice?.device ?? simOrDevice;
  const m = d?.isMonthlyPlanIncluded;
  const y = d?.isYearlyPlanIncluded;
  return {
    ...(typeof m === "boolean" ? { monthly: m } : {}),
    ...(typeof y === "boolean" ? { yearly: y } : {}),
  };
}

export function getDeviceIncludedPlanResolution(
  simData: any,
): DeviceIncludedPlanResolution {
  const { monthly: m, yearly: y } = readPlanInclusionBooleans(simData);
  const hasM = typeof m === "boolean";
  const hasY = typeof y === "boolean";

  if (hasM && hasY) {
    if (m && y) return "both";
    if (m && !y) return "monthly";
    if (!m && y) return "yearly";
    return "neither";
  }

  // Legacy: only `isMonthlyPlanIncluded` — yearly was implied as the inverse (avoid when both API flags exist).
  if (hasM && !hasY) {
    return m ? "monthly" : "yearly";
  }

  if (!hasM && hasY) {
    return y ? "yearly" : "monthly";
  }

  return "unknown";
}

/**
 * True when the device has no home-phone service plan on record (API flags both false).
 * Checkout must use `external-purchase` summary + `create-subscription-external` (not prepaid upsell APIs).
 */
export function isFixedPhoneActivationNoIncludedDevicePlan(
  simData: any,
): boolean {
  return getDeviceIncludedPlanResolution(simData) === "neither";
}

export function getActivationCurrentPlanTypeFromSim(
  simData: any,
): "monthly" | "yearly" {
  const r = getDeviceIncludedPlanResolution(simData);
  if (r === "yearly") return "yearly";
  if (r === "monthly") return "monthly";
  if (r === "both") return "monthly";
  return "monthly";
}

/** True when the included device plan is yearly-only (skips choose-service-plan). Not true when both monthly and yearly are included. */
export function isActivationSimYearlyPlan(simData: any): boolean {
  return getDeviceIncludedPlanResolution(simData) === "yearly";
}

/**
 * Choose-service-plan step: which billing period is pre-included on the device (for "Included" badge and headings).
 * When API includes both monthly and yearly, returns null so neither option is falsely marked as the only included plan.
 */
export function getIncludedPlanHighlightForChooseStep(
  simData: any,
): "monthly" | "yearly" | null {
  const r = getDeviceIncludedPlanResolution(simData);
  if (r === "yearly") return "yearly";
  if (r === "monthly") return "monthly";
  return null;
}

/**
 * Device includes an unlimited monthly component (monthly-only or both flags true).
 * Used for checkout copy such as yearly renewal after upsell from included monthly.
 */
export function isActivationIncludedPlanUnlimitedMonthly(
  simData: any,
): boolean {
  const r = getDeviceIncludedPlanResolution(simData);
  return r === "monthly" || r === "both";
}

/** Yearly / unlimited yearly activation: second-month upsell does not apply; omit `optForSecondMonthUpsell` from APIs. */
export function isActivationYearlyUnlimitedPlan(
  activationData:
    | {
        selectedPlanType?: "monthly" | "yearly";
        simData?: any;
      }
    | null
    | undefined,
): boolean {
  if (!activationData) return false;
  if (activationData.selectedPlanType === "yearly") return true;
  return isActivationSimYearlyPlan(activationData.simData);
}

/**
 * Same persistence as fixed-phone activation choose-service-plan footer “No thanks” /
 * keep current plan: set planId + selectedPlanType from session upsell ids, then caller navigates.
 */
export function persistActivationChooseServicePlanKeepCurrentSelection(): void {
  const activationData = getActivationFlowData();
  const simData = activationData?.simData as any;
  const isYearlyCurrentPlan = isActivationSimYearlyPlan(simData);
  if (isYearlyCurrentPlan) {
    if (activationData?.yearlyPlanId) {
      saveActivationFlowData({ planId: activationData.yearlyPlanId });
    }
    saveActivationFlowData({ selectedPlanType: "yearly" });
  } else {
    if (activationData?.monthlyPlanId) {
      saveActivationFlowData({ planId: activationData.monthlyPlanId });
    }
    saveActivationFlowData({ selectedPlanType: "monthly" });
  }
}

export function getActivationEntryChooseServicePlanPath(): string {
  const flow = getFixedPhonePurchaseFlowData()?.activationPurchaseFlow;
  if (flow === ActivationEntryType.PreloadedSim) {
    return "/activation/preloaded-sim/choose-service-plan";
  }
  return "/activation/fixed-phone/choose-service-plan";
}

export function isSecurityPlanFreeTrialRoutePath(pathname: string): boolean {
  return (
    pathname === "/activation/fixed-phone/security-plan-free-trial" ||
    pathname === "/activation/preloaded-sim/security-plan-upgrade"
  );
}

/**
 * True when session has home-phone plan ids from upsell or choose-service-plan.
 * Without this, checkout / steps after the security trial cannot complete.
 */
export function isActivationServicePlanSeededForFlow(
  activation: ActivationFlowData | null | undefined,
): boolean {
  const purchaseFlow = getFixedPhonePurchaseFlowData()?.activationPurchaseFlow;
  if (purchaseFlow === ActivationEntryType.PreloadedSim) {
    return (
      !!activation?.preloadedSimPrimaryDataTier &&
      !!activation?.preloadedSimServiceFrequency
    );
  }
  if (
    !activation?.planId ||
    !activation.monthlyPlanId ||
    !activation.selectedPlanType
  ) {
    return false;
  }
  if (activation.selectedPlanType === "yearly") {
    return Boolean(activation.yearlyPlanId);
  }
  return true;
}

/**
 * Persists upsell monthly/yearly plan ids and default selection from API plans.
 */
export function applyUpsellPlansToActivationStorage(
  plans: any[],
  currentPlanType: "monthly" | "yearly",
): void {
  if (!plans.length) return;

  const monthlyPlan = plans.find((plan: any) =>
    (plan?.title || "").toLowerCase().includes("monthly"),
  );
  const yearlyPlan = plans.find((plan: any) =>
    (plan?.title || "").toLowerCase().includes("yearly"),
  );
  if (!monthlyPlan?.homePhonePlanId) return;

  saveActivationFlowData({
    monthlyPlanId: monthlyPlan.homePhonePlanId,
    yearlyPlanId: yearlyPlan?.homePhonePlanId,
  });

  const selectedPlanIdInSession = getActivationFlowData()?.planId;
  if (!selectedPlanIdInSession) {
    if (currentPlanType === "yearly" && yearlyPlan?.homePhonePlanId) {
      saveActivationFlowData({
        planId: yearlyPlan.homePhonePlanId,
        selectedPlanType: "yearly",
      });
    } else {
      saveActivationFlowData({
        planId: monthlyPlan.homePhonePlanId,
        selectedPlanType: "monthly",
      });
    }
  }
}
